import type { Match } from "navigo";
import type { ChildDom } from "vanjs-core";
import { reactive } from "vanjs-ext";
import type { Route, RouteContext } from "../router";
import { state } from "../state";
import { centeredBox } from "../van/centered-box";
import { a, button, div, hr, input, li, nav, pre, ul } from "../van/van-tags";
import { fcsPlaceholder } from "./fcs";

export class AdminFcsPage implements Route {
	async #addTrial(): Promise<void> {
		const req = this.#ctx.api.put(
			`/farmcam-sims/${this.#reactive.fcs.Imsi}/trial`,
		);
		req.setJsonBody({
			Bytes: +this.#reactive.trialData * 1e6,
			Days: +this.#reactive.trialDays,
		});
		req.setSignedInUserAuth();

		const res = await this.#ctx.loadingScreen.waitFor(req.do());
		if (res.ok) {
			this.#reactive.fcs = await res.json();
		}
	}

	async #cancelSubscription(): Promise<void> {
		const req = this.#ctx.api.delete(
			`/farmcam-sims/${this.#reactive.fcs.Imsi}/subscription`,
		);
		req.setSignedInUserAuth();

		const res = await this.#ctx.loadingScreen.waitFor(req.do());
		if (res.ok) {
			this.#reactive.fcs = await res.json();
		}
	}

	async #impersonate(): Promise<void> {
		const req = this.#ctx.api.get(
			`/admin/impersonate?key=farmcamsim&value=${this.#reactive.fcs.Imsi}`,
		);
		req.setSignedInUserAuth();

		const res = await this.#ctx.loadingScreen.waitFor(req.do());
		if (res.ok) {
			state.startImpersonating(await res.json());
			this.#ctx.router.jump("/devices");
		}
	}

	async refresh(match: Match): Promise<void> {
		if (!match.data?.imsi) throw "missing path param: imsi";
		const imsi = match.data?.imsi;

		const req = this.#ctx.api.get(`/farmcam-sims/${imsi}`);
		req.setSignedInUserAuth();

		const res = await req.do();
		if (res.ok) {
			this.#reactive.fcs = await res.json();
		}
	}

	#ctx: RouteContext;
	#reactive = reactive({
		fcs: fcsPlaceholder,
		trialData: "",
		trialDays: "",
	});

	ChildDom: ChildDom;

	constructor(ctx: RouteContext) {
		this.#ctx = ctx;
		this.ChildDom = centeredBox(
			{
				size: "narrow",
			},
			nav(
				{
					class: "breadcrumb",
				},
				ul(
					li(
						a(
							{
								onclick: () => this.#ctx.router.jump("/admin"),
							},
							"Admin",
						),
					),
					li(a("FarmCam SIM")),
				),
			),
			div(
				{
					class: "buttons",
				},
				button(
					{
						class: "button is-primary",
						onclick: () => this.#impersonate(),
					},
					"Impersonate owner",
				),
				button(
					{
						class: "button is-primary",
						onclick: () => this.#cancelSubscription(),
					},
					"Cancel subscription",
				),
			),
			hr(),
			div(
				{
					class: "field",
				},
				div(
					{
						class: "control",
					},
					input({
						class: "input",
						oninput: (e: Event) => {
							if (!(e.target instanceof HTMLInputElement)) return;
							this.#reactive.trialData = e.target.value.replace(
								/\D/g,
								"",
							);
							e.target.value = this.#reactive.trialData;
						},
						placeholder: "MB",
						value: () => this.#reactive.trialData,
					}),
				),
			),
			div(
				{
					class: "field",
				},
				div(
					{
						class: "control",
					},
					input(
						{
							class: "input",
							oninput: (e: Event) => {
								if (!(e.target instanceof HTMLInputElement))
									return;
								this.#reactive.trialDays =
									e.target.value.replace(/\D/g, "");
								e.target.value = this.#reactive.trialDays;
							},
							placeholder: "Days",
						},
						() => this.#reactive.trialDays,
					),
				),
			),
			div(
				{
					class: "field",
				},
				div(
					{
						class: "control",
					},
					button(
						{
							class: "button is-primary",
							onclick: () => this.#addTrial(),
						},
						"Add trial",
					),
				),
			),
			hr(),
			pre({ class: "is-tiny-mobile" }, () =>
				JSON.stringify(this.#reactive.fcs, null, 2),
			),
		);
	}
}
